import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static get targets() {
    return ["input", "dataCount", "tagTemplate", "includeKeywords", "excludeKeywords", "tag", "tagsList", "logicalOperator", "selectedOperator"]
  }

  static get values() {
    return { name: String }
  }

  connect() {
    Array.from(this.includeKeywordsTarget.options).forEach((option) => {
      if (option.selected) {
        this._appendTagToElementChildren(option.value)
      }
    })

    Array.from(this.excludeKeywordsTarget.options).forEach((option) => {
      if (option.selected) {
        this._appendTagToElementChildren(option.value, false)
      }
    })

    this._refreshDataCount()
    this._toggleLogicalOperator()
  }

  clear() {
    Array.from(this.includeKeywordsTarget.selectedOptions).forEach(option => {
      const tagValue = option.value
      this._removeTag(tagValue, true)
    })

    Array.from(this.excludeKeywordsTarget.selectedOptions).forEach(option => {
      const tagValue = option.value
      this._removeTag(tagValue, false)
    })

    this._refreshDataCount()
    this._toggleLogicalOperator()
    this._dispatchFilterEvent(true)
  }

  addTag(event) {
    event.preventDefault()

    const value = this.inputTarget.value
    const sanitizedValue = value.replace(/[^\p{L}0-9.]/gu, " ")

    if (sanitizedValue.trim() === "") {
      return
    }

    this._appendTagToElementChildren(sanitizedValue)
    this._addTagToSelect(sanitizedValue, true)
    this._refreshDataCount()
    this._dispatchFilterEvent()
    this._toggleLogicalOperator()
    this.inputTarget.value = ""
  }

  changeTag({ detail: { tagValue, included }}) {
    this._removeTagFromSelect(tagValue, !included)
    this._addTagToSelect(tagValue, included)
    this._refreshDataCount()
    this._dispatchFilterEvent()
    this.tagTargets.forEach(tag => {
      if (tag.dataset.value === tagValue) {
        tag.dataset.type = included ? "included" : "excluded"
      }
    })
    this._toggleLogicalOperator()
  }

  removeTag({ detail: { tagValue, included }}) {
    this._removeTag(tagValue, included)
    this._refreshDataCount()

    // Check if the filter got cleared before dispatching the event
    const isLastTag = this.tagTargets.length === 0
    this._dispatchFilterEvent(isLastTag)
    this._toggleLogicalOperator()
  }

  changeOperator(event) {
    this.selectedOperatorTarget.value = event.target.parentElement.value
    this._dispatchFilterEvent()
  }

  _addTagToSelect(value, included) {
    let option = new Option(value, value, true, true)
    let selectElement = included ? this.includeKeywordsTarget : this.excludeKeywordsTarget
    selectElement.options.add(option)
  }

  _appendTagToElementChildren(value, included = true) {
    let tagHtml = this.tagTemplateTarget.innerHTML
    tagHtml = tagHtml.replace(/%LABEL%/g, value)
    tagHtml = tagHtml.replace(/%VALUE%/g, value)
    tagHtml = tagHtml.replace(/%INCLUDED%/g, included)
    tagHtml = tagHtml.replace(/%TYPE%/g, included ? "included" : "excluded")
    let tagNode = document.createElement('div')
    tagNode.innerHTML = tagHtml

    this.tagsListTarget.appendChild(tagNode.firstElementChild)
  }

  _removeTagFromSelect(tagValue, included) {
    const selectElement = included ? this.includeKeywordsTarget : this.excludeKeywordsTarget
    const index = Array.from(selectElement.options).findIndex((option) => {
      return option.value === tagValue
    })
    selectElement.options.remove(index)
  }

  _removeTag(tagValue, included) {
    this._removeTagFromSelect(tagValue, included)
    this.tagTargets.forEach(tag => {
      if (tag.dataset.value === tagValue) {
        tag.remove()
      }
    })
  }

  // Dispatch the filter event, this can be used with other elements to apply the filters.
  _dispatchFilterEvent(isClearing = false) {
    this.dispatch("filters", { detail: { filters: [
      { name: `${this.nameValue}_included`, values: this._selectedValues(this.includeKeywordsTarget) },
      { name: `${this.nameValue}_excluded`, values: this._selectedValues(this.excludeKeywordsTarget) },
      { name: `${this.nameValue}_operator`, values: [isClearing ? "" : this.selectedOperatorTarget.value] }
    ] } } )
  }

  // Utility method to get all values from a select element.
  _selectedValues(selectElement) {
    return Array.from(selectElement.selectedOptions).map(option => option.value)
  }

  // Refresh the data-count attribute
  _refreshDataCount() {
    if (! this.hasDataCountTarget) {
      return
    }

    let selectedOptions = this.includeKeywordsTarget.selectedOptions.length +
      this.excludeKeywordsTarget.selectedOptions.length

    if (selectedOptions > 0) {
      this.dataCountTarget.dataset.count = selectedOptions
    } else {
      delete this.dataCountTarget.dataset.count
    }
  }

  _toggleLogicalOperator() {
    if (!this.hasLogicalOperatorTarget) {
      return
    }

    const hasAllTagsExcluded = Array.from(this.tagTargets).every(tag => tag.dataset.type === 'excluded')
    const toggleButtons = (disabled, activeButtonValue) => {
      this.logicalOperatorTarget.querySelectorAll('button').forEach(button => {
        button.disabled = disabled
        button.classList.toggle("is-active", button.value === activeButtonValue)
      })
    }

    if (this.tagTargets.length > 1) {
      this.logicalOperatorTarget.classList.remove("d-none")

      if (hasAllTagsExcluded) {
        this.selectedOperatorTarget.value = "and"
        this.logicalOperatorTarget.classList.add("disabled")
        toggleButtons(true, "and")
      } else {
        this.logicalOperatorTarget.classList.remove("disabled")
        toggleButtons(false, this.selectedOperatorTarget.value)
      }
    } else {
      this.logicalOperatorTarget.classList.add("d-none")
    }
  }
}
